<template>
  <div class="profile-search-view">
    <ftu-block
      dismiss-key="profiles"
      icon="badge"
      title="This is the Profile Search"
      description="Profile Search allows you to search the millions of cards in our database organized by the multiple grading companies and presented as profiles. Many of these profiles have been matched together to create a single profile for all grading companies. This page exists to allow you to find all the cards that exist out there and research their population reports and sales histories."
    />

    <page-header title="Profile Search">
      <template #actions>
        <align
          v-if="isStaff && (
            selectedProfilesForMatching.length === 1
            || canMatchProfiles
            || selectedMatchedProfiles.length >= 1
          )"
          small-gap
          class="match-actions"
        >
          <icon-button
            v-if="selectedProfilesForMatching.length === 1 && !selectedMatchedProfiles.length"
            icon="visibility_off"
            tooltip="Exclude this profile from the unmatched match queue"
            :loading="excludingFromMatchQueue"
            @click="excludeSelectedFromMatchQueue"
          />
          <btn
            v-if="selectedMatchedProfiles.length >= 1 && !canMatchProfiles"
            :loading="unmatching"
            loading-text="Unmatching..."
            @click="unmatchProfiles"
          >
            Unmatch {{ selectedMatchedProfiles.length }} profile(s)
          </btn>
          <template v-if="canMatchProfiles">
            <checkbox
              :value="findSetMatches"
              label="Find Set Matches"
              @input="findSetMatches = $event"
            />
            <btn
              :loading="matching"
              loading-text="Matching..."
              @click="matchProfiles"
            >
              Match {{ profilesForMerge.length }} profile(s)
            </btn>
          </template>
        </align>
      </template>
    </page-header>

    <align
      flex
      collapse-end
      direction="flex-start"
      class="search-row"
    >
      <shared-filters
        :filters.sync="filters"
        :query.sync="query"
        :properties="defaultFilters"
        placeholder="Search profiles..."
        @clear="clearFilters"
      />
    </align>

    <filter-results
      :is-filtering="isFiltering"
      :filters="filters"
      :results="numResults != null && numResults < 10000 ? numResults : null"
      @clear="clearFilters"
    />

    <div v-if="isStaff && profilesForMerge.length" class="selected-profiles-strip">
      <div class="selected-profiles-strip-label">
        Selected for matching ({{ profilesForMerge.length }})
      </div>
      <div class="selected-profiles-strip-list">
        <div
          v-for="p in profilesForMerge"
          :key="p.id"
          class="selected-profile-pill"
          :class="{ 'selected-profile-pill--panel-active': isOpenInSidePanel(p.id) }"
        >
          <router-link
            :to="getProfilePanelLocation(p)"
            class="selected-profile-pill-link"
          >
            <span class="selected-profile-pill-name">{{ p.label || p.id || 'Profile' }}</span>
            <span class="selected-profile-pill-graders">{{ gradersParenthetical(p) }}</span>
          </router-link>
          <button
            type="button"
            class="selected-profile-pill-remove"
            aria-label="Remove from selection"
            @click="setProfileSelected(p, false)"
          >
            ×
          </button>
        </div>
      </div>
    </div>

    <div class="results">
      <empty-state
        v-if="showEmptyState"
        icon="search_off"
        title="There are no results for your query"
        description="Try searching for something else"
      />

      <template v-else>
        <div class="list">
          <div
            v-for="(item, i) in visibleResults"
            :key="item.id || i"
            class="profile-card-wrapper"
          >
            <checkbox
              v-if="isStaff"
              :value="selectedProfileIds.includes(item.id)"
              class="profile-checkbox"
              @input="setProfileSelected(item, $event)"
            />

            <router-link
              :to="getProfilePanelLocation(item)"
              class="profile-card"
              :class="{ 'profile-card--panel-active': isOpenInSidePanel(item.id) }"
            >
              <align
                small-gap
                wrap
                direction="center"
                class="profile-card-row"
              >
                <span class="profile-card-label">
                  {{ item.label || item.id || 'No label' }}
                </span>

                <pill
                  v-for="grader in getGraders(item)"
                  :key="grader"
                  readonly
                  no-margin
                >
                  {{ grader }}
                </pill>

                <span v-if="popValue(item) != null" class="pop">
                  Pop: {{ $filters.number(popValue(item)) }}
                </span>
              </align>
            </router-link>
          </div>
        </div>

        <infinite-scroll
          v-if="results.length > 0"
          :loading="loading"
          :has-more-data="hasMoreData"
          @load="nextPage"
        />
      </template>
    </div>

    <scroll-top />

    <div
      v-if="loadingSetSuggestions"
      class="set-suggestions-loading-overlay"
    >
      <spinner inline />
      <span class="set-suggestions-loading-text">Checking for set matches...</span>
    </div>

    <profile-set-matches-modal
      :open="showSetMatchesModal"
      :suggested-set-matches="suggestedSetMatches"
      :profile-info="setMatchProfileInfo"
      @close="onSetMatchesModalClose"
    />
  </div>
</template>

<script>
import debounce from 'lodash.debounce';
import clone from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';
import { mapGetters } from 'vuex';

import { searchService } from '@/services';
import { getHttpStatus } from '@/utils/searchAuth.util';
import { DEFAULT_PROFILE_SEARCH_FILTERS } from '@cardladder/shared/constants';

import FilterResults from '@/components/FilterResults';
import SharedFilters from '@/components/SharedFilters';
import ProfileSetMatchesModal from './ProfileSetMatchesModal.vue';

const GRADER_ID_FIELDS = {
  psaId: 'PSA',
  beckettId: 'BGS',
  sgcId: 'SGC',
  cgcId: 'CGC',
  tagId: 'TAG',
};

export default {
  components: {
    FilterResults,
    SharedFilters,
    ProfileSetMatchesModal,
  },

  data() {
    return {
      limit: 100,
      page: 1,
      hasMoreData: true,
      results: [],
      numResults: null,
      loading: false,
      loaded: false,
      defaultFilters: DEFAULT_PROFILE_SEARCH_FILTERS,
      selectedProfiles: [],
      excludingFromMatchQueue: false,
      matching: false,
      unmatching: false,
      findSetMatches: true,
      loadingSetSuggestions: false,
      showSetMatchesModal: false,
      suggestedSetMatches: [],
      setMatchProfileInfo: null,
    };
  },

  created() {
    this.scheduleReset = debounce(this.reset, 400);
    this.syncQueryFromRoute();
    this.fetchProfiles(true);
  },

  watch: {
    page(value) {
      if (value > 1) {
        this.fetchProfiles(false);
      }
    },

    query() {
      this.scheduleReset();
    },

    filters: {
      handler() {
        this.scheduleReset();
      },
      deep: true,
    },

    '$route.query.q': {
      handler() {
        this.syncQueryFromRoute();
      },
    },
  },

  computed: {
    ...mapGetters({
      isStaff: 'isStaff',
    }),

    query: {
      get() {
        return this.$store.state.profileSearchQuery;
      },

      set(value) {
        this.$store.commit('SET_PROFILE_SEARCH_QUERY', value);
      },
    },

    filters: {
      get() {
        return { ...clone(this.defaultFilters), ...this.$store.state.profileSearchFilterMap };
      },

      set(value) {
        this.$store.commit('SET_PROFILE_SEARCH_FILTERS', value);
      },
    },

    showEmptyState() {
      return !this.loading && this.loaded && this.results.length === 0 && this.query.trim().length > 0;
    },

    selectedProfileIds() {
      return this.selectedProfiles.map((p) => p.id);
    },

    isFiltering() {
      return Boolean(this.query?.trim()) || !isEqual(this.filters, this.defaultFilters);
    },

    selectedProfilesForMatching() {
      return this.selectedProfiles.filter((p) => p.hasMatches !== true);
    },

    selectedMatchedProfiles() {
      return this.selectedProfiles.filter((p) => p.hasMatches === true);
    },

    profilesForMerge() {
      if (this.selectedMatchedProfiles.length && this.selectedProfilesForMatching.length) {
        return [...this.selectedMatchedProfiles, ...this.selectedProfilesForMatching];
      }

      return this.selectedProfilesForMatching;
    },

    canMatchProfiles() {
      const toMerge = this.profilesForMerge;
      if (toMerge.length < 2) return false;

      const hasPsa = toMerge.some((item) => this.getGraders(item).includes('PSA'));
      if (!hasPsa) return false;

      const graders = new Set();
      for (const profile of toMerge) {
        for (const grader of this.getGraders(profile)) {
          if (graders.has(grader)) return false;
          graders.add(grader);
        }
      }

      if (this.selectedMatchedProfiles.length && this.selectedProfilesForMatching.length) {
        return true;
      }

      return this.selectedProfilesForMatching.length >= 2;
    },

    /** Graders in the match selection (one profile per grader). */
    selectedGraderSet() {
      const set = new Set();
      for (const p of this.profilesForMerge) {
        for (const g of this.getGraders(p)) {
          set.add(g);
        }
      }
      return set;
    },

    /** Hides duplicate grader rows while matching solo profiles; selected rows stay visible. */
    visibleResults() {
      if (this.selectedGraderSet.size === 0) {
        return this.results;
      }
      const selectedIds = new Set(this.selectedProfileIds);
      return this.results.filter((item) => {
        if (selectedIds.has(item.id)) return true;
        const graders = this.getGraders(item);
        return !graders.some((g) => this.selectedGraderSet.has(g));
      });
    },
  },

  methods: {
    getGraders(item) {
      const graders = [];

      for (const [field, label] of Object.entries(GRADER_ID_FIELDS)) {
        if (item[field] != null && item[field] !== '') {
          graders.push(label);
        }
      }

      return graders;
    },

    gradersParenthetical(item) {
      const graders = this.getGraders(item);
      return graders.length ? ` (${graders.join(', ')})` : '';
    },

    popValue(item) {
      return item.totalPopulation ?? item.total_population ?? null;
    },

    yearSetNumberSearchText(item) {
      const parts = [item.year, item.set, item.number]
        .map((p) => (p != null && String(p).trim() !== '' ? String(p).trim() : null))
        .filter(Boolean);
      return parts.join(' ');
    },

    syncQueryFromRoute() {
      const routeQuery = (this.$route.query.q || '').trim();
      if (!routeQuery) return;
      if (routeQuery !== this.query) {
        this.query = routeQuery;
      }
    },

    reset() {
      this.page = 1;
      this.hasMoreData = true;
      this.loaded = false;
      this.fetchProfiles(true);
    },

    nextPage() {
      if (this.hasMoreData && !this.loading) {
        this.page += 1;
      }
    },

    async fetchProfiles(reset) {
      try {
        this.loading = true;
        if (reset) {
          this.loaded = false;
        }

        const filters = {
          ...this.filters,
          hasProfileRedirect: false,
        };

        if (this.isStaff && filters.hasMatches === false) {
          filters.excludeFromMatches = false;
        }

        const data = await searchService.search({
          index: 'profiles',
          query: this.query.trim(),
          sort: 'totalPopulation',
          direction: 'desc',
          page: this.page,
          limit: this.limit,
          filters,
        });

        const hits = data.hits || [];

        this.numResults = data.totalHits ?? null;

        if (reset) {
          this.results = hits;
        } else {
          this.results = [...this.results, ...hits];
        }

        if (hits.length < this.limit) {
          this.hasMoreData = false;
        }
      } catch (e) {
        if (getHttpStatus(e) === 429) {
          this.$toasted.error('Too many searches. Please wait and try again.');
        } else {
          this.$toasted.error('Error searching profiles');
        }
        Sentry.captureException(e);
        if (reset) {
          this.results = [];
        }
        this.hasMoreData = false;
      }

      this.loaded = true;
      this.loading = false;
    },

    clearFilters() {
      this.filters = clone(this.defaultFilters);
    },

    async excludeSelectedFromMatchQueue() {
      if (!this.isStaff) return;
      const ids = this.selectedProfileIds.filter(Boolean);
      if (!ids.length) return;

      const ok = window.confirm(
        `Exclude ${ids.length} profile(s) from the unmatched match queue? They will no longer appear when filtering to profiles that still need matching.`,
      );
      if (!ok) return;

      this.excludingFromMatchQueue = true;

      try {
        const { data } = await firebase.functions().httpsCallable('httpexcludeprofilefrommatch')({
          profileIds: ids,
          excludeFromMatches: true,
        });

        const totalUpdated = data.updated || 0;
        const skipped = Array.isArray(data.skipped) ? data.skipped : [];

        const idSet = new Set(ids);
        this.results = this.results.filter((r) => !idSet.has(r.id));
        this.selectedProfiles = [];

        let msg = `Excluded ${totalUpdated} profile(s) from the match queue`;
        if (skipped.length) {
          msg += ` (${skipped.length} skipped: merged, redirected, or missing)`;
        }
        this.$toasted.success(msg);
        this.query = '';
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to exclude profiles');
        Sentry.captureException(e);
      } finally {
        this.excludingFromMatchQueue = false;
      }
    },

    getProfilePanelLocation(item) {
      const query = { ...this.$route.query, profileId: item.id };
      delete query.profileGrader;
      delete query.profileGrade;
      delete query.profilePopulationType;
      return { query };
    },

    isOpenInSidePanel(profileId) {
      const openId = this.$route.query.profileId;
      if (openId == null || openId === '') return false;
      return String(openId) === String(profileId);
    },

    setProfileSelected(item, selected) {
      if (!this.isStaff) return;

      const profileId = item.id;
      if (selected) {
        const next = clone(item);
        const idx = this.selectedProfiles.findIndex((p) => p.id === profileId);
        const isFirstSelection = idx === -1 && this.selectedProfiles.length === 0;
        if (isFirstSelection && !next.hasMatches && !this.query.trim()) {
          const seed = this.yearSetNumberSearchText(next);
          if (seed) {
            this.query = seed;
          }
        }
        if (idx === -1) {
          this.selectedProfiles = [...this.selectedProfiles, next];
        } else {
          const copy = [...this.selectedProfiles];
          copy[idx] = next;
          this.selectedProfiles = copy;
        }
      } else {
        this.selectedProfiles = this.selectedProfiles.filter((p) => p.id !== profileId);
      }
    },

    onSetMatchesModalClose() {
      this.showSetMatchesModal = false;
      this.suggestedSetMatches = [];
      this.setMatchProfileInfo = null;
      this.reset();
    },

    async unmatchProfiles() {
      if (!this.isStaff) return;

      const toUnmatch = this.selectedMatchedProfiles;
      if (!toUnmatch.length) return;

      const count = toUnmatch.length;
      const noun = count === 1 ? 'profile' : 'profiles';
      const ok = window.confirm(
        `Unmatch ${count} ${noun}? Solo profiles will be cleared of the canonical link and the merged ${noun} will be deleted.`,
      );
      if (!ok) return;

      this.unmatching = true;

      try {
        const db = firebase.firestore();
        const BATCH_SIZE = 500;

        for (let i = 0; i < toUnmatch.length; i += BATCH_SIZE) {
          const chunk = toUnmatch.slice(i, i + BATCH_SIZE);
          const batch = db.batch();
          for (const p of chunk) {
            batch.delete(db.collection('profiles').doc(p.id));
          }
          await batch.commit();
        }

        const idSet = new Set(toUnmatch.map((p) => p.id));
        this.results = this.results.filter((r) => !idSet.has(r.id));
        this.selectedProfiles = this.selectedProfiles.filter((p) => !idSet.has(p.id));

        this.$toasted.success(`Unmatched ${count} profile(s)`);
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to unmatch profiles');
        Sentry.captureException(e);
      } finally {
        this.unmatching = false;
      }
    },

    async matchProfiles() {
      if (!this.isStaff) return;

      const toMatch = this.profilesForMerge;
      if (!this.canMatchProfiles) return;

      const isAppend = this.selectedMatchedProfiles.length > 0 && this.selectedProfilesForMatching.length > 0;
      const ok = window.confirm(
        isAppend
          ? `Add ${this.selectedProfilesForMatching.length} profile(s) to the existing matched profile?\n\nThis will merge them into one master profile.`
          : `Match ${toMatch.length} profiles?\n\nThis will merge them into one master profile.`,
      );
      if (!ok) return;

      const profiles = toMatch.map((item) => {
        const entry = { profileId: item.id };
        const grader =
          (item.graderList && item.graderList[0]) || (item.graders && Object.keys(item.graders)[0]);

        if (grader) {
          entry.grader = grader.toLowerCase();
        }

        return entry;
      });

      try {
        this.matching = true;
        const employeeId = this.$store?.state?.user?.uid || null;

        const { data } = await firebase.functions().httpsCallable('httpmatchprofiles')({
          profiles,
          employeeId,
          reportType: 'PROFILE_MATCHED_MANUAL',
        });
        const message = data.updatedExisting
          ? `Profiles merged; ${data.redirectedProfileIds?.length ?? 0} redirected to existing profile`
          : `Created merged profile; ${data.redirectedProfileIds?.length ?? 0} profiles redirected`;

        this.$toasted.success(message);
        this.selectedProfiles = [];

        if (this.findSetMatches) {
          const canonicalProfileId = data.canonicalProfileId;
          this.loadingSetSuggestions = true;
          const suggestSnap = await firebase.functions().httpsCallable('httpsuggestsetprofilematches')({
            canonicalProfileId,
            employeeId,
          });

          const rawSuggested = suggestSnap?.data?.suggestedSetMatches || [];
          const rawProfileInfo = suggestSnap?.data?.profileInfo || null;

          if (rawSuggested.length > 0) {
            this.setMatchProfileInfo = rawProfileInfo ? clone(rawProfileInfo) : null;
            this.suggestedSetMatches = clone(rawSuggested);

            this.$nextTick(() => {
              this.showSetMatchesModal = true;
            });
          }
        }

        setTimeout(() => this.reset(), 3000);
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to match profiles');
        Sentry.captureException(e);
      } finally {
        this.loadingSetSuggestions = false;
        this.matching = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.profile-search-view {
  @include container;
  min-height: 100%;
}

.match-actions {
  --checkbox-margin-bottom: 0;
}

.search-row {
  margin-bottom: $gp;
}

.selected-profiles-strip {
  margin-top: $gp;
  padding: $gp;
  border-radius: $border-radius;
  border: 1px solid var(--theme-border);
  background-color: var(--theme-card);
}

.selected-profiles-strip-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--theme-color);
  margin-bottom: $gp * 0.5;
  opacity: 0.9;
}

.selected-profiles-strip-list {
  display: flex;
  flex-wrap: wrap;
  gap: $gp * 0.5;
  align-items: center;
}

.selected-profile-pill {
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  border-radius: $border-radius;
  border: 1px solid var(--theme-active);
  background-color: var(--theme-highlight);
  overflow: hidden;

  &--panel-active {
    box-shadow: 0 0 0 2px var(--theme-active);
  }
}

.selected-profile-pill-link {
  flex: 1;
  min-width: 0;
  padding: 6px 10px;
  font-size: 14px;
  font-weight: 500;
  color: var(--theme-color);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.selected-profile-pill-name {
  font-weight: 600;
}

.selected-profile-pill-graders {
  font-weight: 500;
  opacity: 0.85;
}

.selected-profile-pill-remove {
  flex-shrink: 0;
  margin: 0;
  padding: 4px 10px;
  border: none;
  border-left: 1px solid var(--theme-border);
  background: transparent;
  color: var(--theme-color);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  opacity: 0.75;

  &:hover {
    opacity: 1;
    background-color: var(--theme-background);
  }
}

.results {
  position: relative;
  flex: 1;
  margin-top: $gp;
}

.list {
  margin-top: $gp;
  display: flex;
  flex-direction: column;
  gap: $gp * 0.5;
}

.profile-card-wrapper {
  display: flex;
  align-items: center;
  gap: $gp;
}

.profile-checkbox {
  flex-shrink: 0;
  --checkbox-margin-bottom: 0;
}

.profile-card {
  flex: 1;
  display: block;
  padding: $gp;
  color: var(--theme-color);
  text-decoration: none;
  border-radius: $border-radius;
  border: 1px solid var(--theme-border);
  background-color: var(--theme-card);
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background-color: var(--theme-highlight);
    border-color: var(--theme-active);
  }

  &--panel-active {
    background-color: var(--theme-active);
    border-color: var(--theme-active);

    &:hover {
      background-color: var(--theme-active);
      border-color: var(--theme-active);
    }
  }
}

.profile-card-row {
  flex-wrap: wrap;
  align-items: center;
}

.profile-card-label {
  font-weight: 600;
}

.pop {
  font-size: 14px;
  color: var(--theme-color);
}

.set-suggestions-loading-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $gp;
  background: var(--theme-background);
  opacity: 0.95;
}

.set-suggestions-loading-text {
  font-size: 15px;
  color: var(--theme-color);
}
</style>
